import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Copy, Trash2, Eye, Search, FileDown, X, MessageSquareCode, Heart, ArrowLeft, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function TextLibrary() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [previewItem, setPreviewItem] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [tab, setTab] = useState("all");
  const canExportPdf = user && ["basic", "plus", "pro"].includes(user.plan);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (tab === "favorites") params.favorites_only = true;
      if (debouncedQuery) params.q = debouncedQuery;
      const { data } = await api.get("/text-prompts", { params });
      setItems(data);
    } catch (err) { toast.error(errorMessage(err, t)); }
  }, [tab, debouncedQuery, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const to = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(to);
  }, [searchQuery]);

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(t("copied") || "Copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/text-prompts/${id}`);
      toast.success(t("deleted") || "Deleted");
      load();
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const toggleFavorite = async (p) => {
    try {
      await api.patch(`/text-prompts/${p.text_prompt_id}`, { is_favorite: !p.is_favorite });
      load();
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const exportPdf = async (p) => {
    if (!canExportPdf) { toast.error(t("export_paid_only") || "Available on paid plans"); return; }
    try {
      const { data } = await api.get(`/text-prompts/${p.text_prompt_id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const safeTitle = (p.title || "prompt").replace(/[^\w\-. ]+/g, "").trim().slice(0, 40) || "prompt";
      const a = document.createElement("a"); a.href = url; a.download = `vixelforge-${safeTitle}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const startEditTitle = (p) => {
    setEditingTitleId(p.text_prompt_id);
    setTitleDraft(p.title || "");
  };

  const saveTitle = async () => {
    if (!editingTitleId) return;
    try {
      await api.patch(`/text-prompts/${editingTitleId}`, { title: titleDraft.slice(0, 70) });
      setEditingTitleId(null);
      setTitleDraft("");
      toast.success(t("saved") || "Saved");
      load();
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const editPrompt = (p) => {
    // Load this prompt back into the AI Prompt Assistant with all fields prefilled
    navigate(`/app/text-assistant?edit=${p.text_prompt_id}`);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-white flex items-center gap-3">
            <MessageSquareCode className="w-8 h-8 text-sky-400" />
            {t("txtlib_title") || "Assistant Library"}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">{t("txtlib_subtitle") || "Your saved LLM prompts, separate from image and video prompts."}</p>
        </div>
        <Link to="/app/text-assistant" data-testid="txtlib-back-to-generator" className="text-sm text-orange-400 hover:text-orange-300 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> {t("nav_text_assistant") || "AI Prompt Assistant"}
        </Link>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[{ k: "all", label: t("all") }, { k: "favorites", label: t("favorites") }].map((f) => (
          <button key={f.k} onClick={() => setTab(f.k)} data-testid={`txtlib-tab-${f.k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${tab === f.k ? "bg-orange-500 text-black" : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          data-testid="txtlib-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("search_prompts_ph") || "Search…"}
          maxLength={200}
          className="pl-10 pr-9 bg-[#0E0E12]/40 border-white/10 h-10 text-sm"
        />
        {searchQuery && (
          <button type="button" onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center text-zinc-500 py-16">
          <MessageSquareCode className="w-14 h-14 mx-auto mb-4 opacity-40" />
          <p>{t("txtlib_empty") || "No text prompts yet. Generate your first one from the AI Prompt Assistant."}</p>
          <Link to="/app/text-assistant" className="text-orange-400 hover:text-orange-300 text-sm mt-3 inline-block">
            {t("txtlib_go_generate") || "Open the assistant →"}
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.text_prompt_id} className="rounded-md border border-white/10 bg-white/[0.02] hover:border-white/20 p-4 md:p-5" data-testid={`txtlib-item-${p.text_prompt_id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {editingTitleId === p.text_prompt_id ? (
                    <input
                      autoFocus
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onBlur={saveTitle}
                      onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") { setEditingTitleId(null); setTitleDraft(""); } }}
                      maxLength={70}
                      data-testid={`txtlib-title-input-${p.text_prompt_id}`}
                      className="bg-transparent border-b border-orange-500/50 outline-none text-white font-semibold w-full"
                    />
                  ) : (
                    <button
                      onClick={() => startEditTitle(p)}
                      data-testid={`txtlib-title-btn-${p.text_prompt_id}`}
                      className="text-white font-semibold truncate text-left w-full hover:text-orange-400 transition-colors"
                      title={t("edit_title") || "Click to rename"}
                    >
                      {p.title || (t("untitled_prompt") || "Untitled")}
                    </button>
                  )}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-sky-400 font-bold">{p.category_label}</span>
                    <span className="text-xs text-zinc-500">{new Date(p.created_at).toLocaleString(lang)}</span>
                  </div>
                  <div className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.generated_prompt}</div>
                </div>
                <div className="flex gap-1 flex-wrap justify-end md:justify-start md:flex-nowrap shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setPreviewItem(p)} data-testid={`txtlib-preview-${p.text_prompt_id}`} className="text-zinc-400 hover:text-orange-400"><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => editPrompt(p)} data-testid={`txtlib-edit-${p.text_prompt_id}`} className="text-zinc-400 hover:text-orange-400" title={t("edit_prompt") || "Edit prompt"}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => copy(p.generated_prompt)} data-testid={`txtlib-copy-${p.text_prompt_id}`} className="text-zinc-400 hover:text-white"><Copy className="w-4 h-4" /></Button>
                  {canExportPdf && (
                    <Button size="sm" variant="ghost" onClick={() => exportPdf(p)} data-testid={`txtlib-pdf-${p.text_prompt_id}`} className="text-zinc-400 hover:text-orange-400"><FileDown className="w-4 h-4" /></Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => toggleFavorite(p)} data-testid={`txtlib-fav-${p.text_prompt_id}`} className={p.is_favorite ? "text-red-400" : "text-zinc-400 hover:text-red-400"}><Heart className={`w-4 h-4 ${p.is_favorite ? "fill-red-400" : ""}`} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p.text_prompt_id)} data-testid={`txtlib-del-${p.text_prompt_id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="bg-[#121218] border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="txtlib-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">{previewItem?.title}</DialogTitle>
            {previewItem && (
              <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mt-1">
                {previewItem.category_label}
                <span className="text-zinc-500 ml-2 normal-case tracking-normal font-normal">{new Date(previewItem.created_at).toLocaleString(lang)}</span>
              </div>
            )}
          </DialogHeader>
          <div className="mt-3 max-h-[55vh] overflow-y-auto bg-[#0E0E12]/40 border border-white/5 rounded p-4">
            <pre className="whitespace-pre-wrap break-words text-sm text-zinc-100 font-mono leading-relaxed">{previewItem?.generated_prompt}</pre>
          </div>
          <DialogFooter className="mt-4 gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => previewItem && copy(previewItem.generated_prompt)} className="bg-white/5 border-white/10 hover:bg-white/10 text-white w-full sm:w-auto">
              <Copy className="w-4 h-4 mr-2" /> {t("copy") || "Copy"}
            </Button>
            <Button onClick={() => setPreviewItem(null)} className="bg-orange-500 hover:bg-orange-600 text-black font-bold w-full sm:w-auto">
              {t("close") || "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
